import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Box, Button, Grid, Typography } from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import EditRounded from "@mui/icons-material/EditRounded";
import {
  hasPermission,
  IVacationBidPackageListItem,
  PolicyAction,
  PolicyResource,
  VacationBidPackageStatus,
} from "@vmosolutions/oc-interfaces";
// Components
import { FilterSearchTable } from "src/components/FilterSearchTable/FilterSearchTable";
import { ITableColumn } from "src/components/FilterSearchTable/TableComponents/TableHeader";
import { updateAirportOptions } from "src/utils/airportHelper";
import {
  VACATION_PACKAGE_DATA_TYPE,
  VACATION_PACKAGE_STATUS_COLORS,
} from "src/components/FilterSearchTable/TableComponents/TableCellContent";
import { SelectField } from "src/components/FormikComponents/SelectField";
import { SpinnerProgress } from "src/components/SpinnerProgress/SpinnerProgress";
// Redux
import { IRootState } from "src/redux/reducers/CombinedReducers";
import { openWindow } from "src/redux/actions/Mosaic.Actions";
// Vacation bidding
import { getVacationBidPackages } from "./helpers";
import { windowSummaryLabel } from "./ChangeRequests/changeRequestHelpers";
import { VACATION_BID_TEST_ID } from "./testIds";
import {
  IStatusFilterOption,
  IVacationBidPackageEditorWindowProps,
  IVacationBidPackageListWindowProps,
  WINDOW_TITLE,
} from "./types";

/////////////////////////////////////////////////////
/////             STATIC TABLE DATA            //////
/////////////////////////////////////////////////////

/** Epoch milliseconds for the timezone-aware cell renderer, which only formats numbers. */
const toEpoch = (value: Date | string | null | undefined): number | null => {
  if (!value) return null;
  const time = new Date(value).valueOf();
  return Number.isNaN(time) ? null : time;
};

const packageTableDesc = {
  tableTitle: "Vacation Bid Packages",
  tableDescription: "",
  noMessage: "No vacation bid packages to display.",
};

const packageTableInfo: ITableColumn[] = [
  {
    id: "_id",
    value: "_id",
    index: 0,
    numeric: false,
    label: "id",
    type: "string",
    width: 1,
    visible: false,
    filter: false,
  },
  {
    id: "title",
    value: "title",
    index: 1,
    numeric: false,
    label: "Title",
    type: "string",
    width: 10,
    visible: true,
    filter: false,
  },
  {
    id: "status",
    value: "status",
    index: 2,
    numeric: false,
    label: "Status",
    type: "string",
    width: 10,
    visible: true,
    filter: false,
  },
  {
    id: "changeWindowStatus",
    value: "changeWindowStatus",
    index: 3,
    numeric: false,
    label: "Change Window",
    type: "string",
    width: 10,
    visible: true,
    filter: false,
  },
  {
    id: "baseAirport",
    value: "baseAirport",
    index: 4,
    numeric: false,
    label: "Base",
    type: "string",
    width: 6,
    visible: true,
    filter: false,
  },
  {
    id: "crewGroups",
    value: "crewGroups",
    index: 5,
    numeric: false,
    label: "Crew Groups",
    type: "array",
    width: 10,
    visible: true,
    filter: false,
  },
  {
    id: "bidWindowOpen",
    value: "bidWindowOpen",
    index: 6,
    numeric: false,
    label: "Bid Window Open",
    type: "epochWithTimezone",
    width: 10,
    visible: true,
    filter: false,
  },
  {
    id: "bidWindowClose",
    value: "bidWindowClose",
    index: 7,
    numeric: false,
    label: "Bid Window Close",
    type: "epochWithTimezone",
    width: 10,
    visible: true,
    filter: false,
  },
  {
    id: "secondBidWindowOpen",
    value: "secondBidWindowOpen",
    index: 8,
    numeric: false,
    label: "Second Window Open",
    type: "epochWithTimezone",
    width: 10,
    visible: true,
    filter: false,
  },
  {
    id: "secondBidWindowClose",
    value: "secondBidWindowClose",
    index: 9,
    numeric: false,
    label: "Second Window Close",
    type: "epochWithTimezone",
    width: 10,
    visible: true,
    filter: false,
  },
  {
    id: "bidBlockStart",
    value: "bidBlockStart",
    index: 10,
    numeric: false,
    label: "Bid Block Start",
    type: "epochWithTimezone",
    width: 10,
    visible: true,
    filter: false,
  },
  {
    id: "bidBlockEnd",
    value: "bidBlockEnd",
    index: 11,
    numeric: false,
    label: "Bid Block End",
    type: "epochWithTimezone",
    width: 10,
    visible: true,
    filter: false,
  },
  {
    id: "maxAwardsFirstRound",
    value: "maxAwardsFirstRound",
    index: 12,
    numeric: true,
    label: "Max Awards Round 1",
    type: "number",
    width: 10,
    visible: true,
    filter: false,
  },
  {
    id: "slotCount",
    value: "slotCount",
    index: 13,
    numeric: true,
    label: "Slot Count",
    type: "number",
    width: 10,
    visible: true,
    filter: false,
  },
  {
    id: "crewCount",
    value: "crewCount",
    index: 14,
    numeric: true,
    label: "Crew Count",
    type: "number",
    width: 10,
    visible: true,
    filter: false,
  },
];

/**
 * A list item shaped for the table: window and block times as epochs, carrying the zone the cell
 * renderer formats them in.
 */
type IVacationBidPackageRow = Omit<
  IVacationBidPackageListItem,
  "bidWindowOpen" | "bidWindowClose" | "secondBidWindowOpen" | "secondBidWindowClose" | "bidBlockStart" | "bidBlockEnd"
> & {
  crewBaseTimezone?: string;
  changeWindowStatus: string;
  bidWindowOpen: number | null;
  bidWindowClose: number | null;
  secondBidWindowOpen: number | null;
  secondBidWindowClose: number | null;
  bidBlockStart: number | null;
  bidBlockEnd: number | null;
};

interface IPackageTableDataSet {
  id: string;
  dataType: string;
  label: string;
  data: IVacationBidPackageRow[];
  tableInfo: ITableColumn[];
  tableInfoDisplay: typeof packageTableDesc;
}

/////////////////////////////////////////////////////
/////             STATUS FILTER                //////
/////////////////////////////////////////////////////

const SHOW_ALL_FILTER_VALUE = "all";
const defaultFilter: IStatusFilterOption = { label: "Show All", value: SHOW_ALL_FILTER_VALUE, color: "white" };

const statusFilterOptions: IStatusFilterOption[] = [
  defaultFilter,
  ...Object.values(VacationBidPackageStatus).map(
    (status): IStatusFilterOption => ({
      label: status,
      value: status,
      color: VACATION_PACKAGE_STATUS_COLORS[status] ?? null,
    })
  ),
];

/** Rows carry their status from the backend; the filter only matches against it. */
const filterPackagesByStatus = (
  packages: IVacationBidPackageListItem[],
  filter: IStatusFilterOption
): IVacationBidPackageListItem[] => {
  if (filter.value === SHOW_ALL_FILTER_VALUE) return packages;
  return packages.filter((row) => (row.status as string | undefined) === filter.value);
};

const formatStatusOptionLabel = (option: IStatusFilterOption): JSX.Element => (
  <Typography variant="caption" style={{ color: option.color ?? undefined }}>
    {option.label}
  </Typography>
);

/////////////////////////////////////////////////////
/////             LIST WINDOW                  //////
/////////////////////////////////////////////////////

export const VacationBidPackageListWindow = (props: IVacationBidPackageListWindowProps): JSX.Element => {
  const { id, path } = props;
  const dispatch = useDispatch();
  const mosaicPanelId = useMemo(() => `${id}-panel`, [id]);
  const mosaicPane = useMemo(() => (id ? document.getElementById(mosaicPanelId) : null), [id, mosaicPanelId]);

  // Permissions
  const policies = useSelector((state: IRootState) => state.authentication?.attributes?.policies);
  const canCreatePackage = hasPermission(policies, PolicyResource.VacationBidding, PolicyAction.Create);
  const canUpdatePackage = hasPermission(policies, PolicyResource.VacationBidding, PolicyAction.Update);

  // Package list, held locally and refetched on demand.
  const [packages, setPackages] = useState<IVacationBidPackageListItem[]>([]);
  const [isLoadingPackages, setIsLoadingPackages] = useState<boolean>(false);
  const [selectedPackage, setSelectedPackage] = useState<IVacationBidPackageListItem | null>(null);
  const [statusFilter, setStatusFilter] = useState<IStatusFilterOption>(defaultFilter);

  const loadPackages = useCallback(async (): Promise<void> => {
    setIsLoadingPackages(true);
    setSelectedPackage(null);
    const response = await getVacationBidPackages();
    setPackages(Array.isArray(response) ? response : []);
    setIsLoadingPackages(false);
  }, []);

  const handleRefresh = useCallback((): void => {
    void loadPackages();
  }, [loadPackages]);

  // Load the list once when the window mounts.
  useEffect(() => {
    void loadPackages();
  }, [loadPackages]);

  // The cell renderer reads a row's zone from `crewBaseTimezone`, which is resolved from the
  // package's base airport. Airports arrive in the store lazily, so each one in the list is
  // requested once.
  const airportOptions = useSelector((state: IRootState) => state.structures.airportOptions);

  useEffect(() => {
    const idents = new Set(packages.flatMap((pkg) => (pkg.baseAirport ? [pkg.baseAirport] : [])));
    idents.forEach((ident) => {
      void updateAirportOptions(ident, airportOptions, dispatch);
    });
  }, [packages, airportOptions, dispatch]);

  /**
   * Table rows carry epoch times plus the zone they belong to, so each package's windows and block
   * read in its own base local time. A package with no base keeps UTC.
   */
  const packageRows: IVacationBidPackageRow[] = useMemo(
    () =>
      filterPackagesByStatus(packages, statusFilter).map((pkg) => ({
        ...pkg,
        crewBaseTimezone: pkg.baseAirport ? airportOptions[pkg.baseAirport]?.timezoneId : undefined,
        changeWindowStatus: windowSummaryLabel(pkg.changeWindow),
        bidWindowOpen: toEpoch(pkg.bidWindowOpen),
        bidWindowClose: toEpoch(pkg.bidWindowClose),
        secondBidWindowOpen: toEpoch(pkg.secondBidWindowOpen),
        secondBidWindowClose: toEpoch(pkg.secondBidWindowClose),
        bidBlockStart: toEpoch(pkg.bidBlockStart),
        bidBlockEnd: toEpoch(pkg.bidBlockEnd),
      })),
    [packages, statusFilter, airportOptions]
  );

  // Memoised so the table does not rebuild its rows on unrelated renders.
  const tableData: IPackageTableDataSet[] = useMemo(
    () => [
      {
        id: VACATION_PACKAGE_DATA_TYPE,
        dataType: VACATION_PACKAGE_DATA_TYPE,
        label: packageTableDesc.tableTitle,
        data: packageRows,
        tableInfo: packageTableInfo,
        tableInfoDisplay: packageTableDesc,
      },
    ],
    [packageRows]
  );

  // Create a new package, or a copy of the selected one.
  const createPackage = useCallback((): void => {
    const windowProps: IVacationBidPackageEditorWindowProps["windowProps"] = {
      vacationBidPackageListItem: selectedPackage ?? undefined,
      duplicate: !!selectedPackage,
    };
    dispatch(openWindow(WINDOW_TITLE.newPackage, "VacationBidPackageEditorWindow", [], windowProps, "content"));
  }, [dispatch, selectedPackage]);

  const editPackage = useCallback((): void => {
    if (!selectedPackage) return;
    const windowProps: IVacationBidPackageEditorWindowProps["windowProps"] = {
      vacationBidPackageListItem: selectedPackage,
    };
    // The editor only retitles its own tab after a save, so an edit opens under the package's name
    // from the outset rather than sitting on the generic title until something is saved.
    const title = selectedPackage.title || WINDOW_TITLE.editPackage;
    dispatch(openWindow(title, "VacationBidPackageEditorWindow", [], windowProps, "content"));
  }, [dispatch, selectedPackage]);

  /** Single-select: the table hands back at most one row. */
  const handleSelectPackage = useCallback(
    (rows: IVacationBidPackageRow[], focusSelected?: boolean, dataType?: string): void => {
      if (dataType !== VACATION_PACKAGE_DATA_TYPE) return;
      // Rows carry epoch times for display; the editor wants the package as the API returned it.
      const selectedId = rows?.length ? rows[0]?._id : undefined;
      setSelectedPackage(packages.find((pkg) => pkg._id === selectedId) ?? null);
    },
    [packages]
  );

  const handleStatusFilterChange = useCallback((option: IStatusFilterOption | null): void => {
    setStatusFilter(option ?? defaultFilter);
    // The selected row may not survive the new filter, and the controls act on the selection.
    setSelectedPackage(null);
  }, []);

  return (
    <Box
      data-testid={VACATION_BID_TEST_ID.packageList}
      sx={(theme) => ({
        position: "relative",
        padding: "15px",
        width: "100%",
        "& .vacation_bid_control_btn": {
          fontSize: 12,
          padding: "2px 8px",
          marginRight: "10px",
          "& svg": {
            width: "12px",
            height: "12px",
            marginRight: "5px",
          },
        },
        "& .status_filter_container": {
          marginLeft: "15px",
          width: "auto",
          "& .status_filter_label": {
            color: theme.custom.forms.labels,
            whiteSpace: "nowrap",
          },
          "& .status_filter_field": {
            width: 300,
            marginLeft: "10px",
          },
        },
      })}
    >
      <SpinnerProgress className="loadingSpinner" loading={isLoadingPackages} message="loading vacation bid packages" />
      <FilterSearchTable
        id={id}
        path={path}
        className="vacation_bid_package_management"
        events={tableData}
        parentId={mosaicPanelId}
        uniqueKey="_id"
        allowMultiFilter={true}
        hideFocusSelected={true}
        changeSelectedRows={handleSelectPackage}
        selectModeSingle={true}
        defaultOrderKey="bidBlockStart"
        showUTCLocalToggle={true}
        showEventActions={true}
        defaultRowsPerPage={100}
        optionalControls={
          <Grid container style={{ width: "auto" }}>
            <Button
              id="vacation_bid_refresh_btn"
              data-testid={VACATION_BID_TEST_ID.refreshButton}
              variant="contained"
              color="primary"
              disabled={isLoadingPackages}
              onClick={handleRefresh}
              className="vacation_bid_control_btn"
            >
              {packages.length === 0 ? "Load Vacation Bid Packages" : "Refresh"}
            </Button>
            <Button
              id="vacation_bid_create_btn"
              data-testid={VACATION_BID_TEST_ID.createButton}
              variant="outlined"
              disabled={!canCreatePackage}
              onClick={createPackage}
              className="vacation_bid_control_btn"
            >
              <OpenInNewIcon />
              {selectedPackage ? "DUPLICATE SELECTED" : "CREATE NEW VACATION BID PACKAGE"}
            </Button>
            <Button
              id="vacation_bid_edit_btn"
              data-testid={VACATION_BID_TEST_ID.editButton}
              variant="outlined"
              disabled={!canUpdatePackage || !selectedPackage}
              onClick={editPackage}
              className="vacation_bid_control_btn"
            >
              <EditRounded />
              MANAGE PACKAGE
            </Button>

            <Grid container alignItems="center" className="status_filter_container" wrap="nowrap">
              <Typography variant="caption" className="status_filter_label">
                Filter By Package Status
              </Typography>
              <Grid item className="status_filter_field">
                <SelectField
                  className="field selectfield status_filter"
                  name="vacation_bid_package_status_filter"
                  data-testid={VACATION_BID_TEST_ID.statusFilter}
                  label=""
                  isMulti={false}
                  formatOptionLabel={formatStatusOptionLabel}
                  options={statusFilterOptions}
                  value={statusFilter}
                  onChange={handleStatusFilterChange}
                  menuPortalTarget={mosaicPane}
                />
              </Grid>
            </Grid>
          </Grid>
        }
      />
    </Box>
  );
};
